// Connections hooks — the /settings/connections tab over the generic connect surface,
// hand-written over the generated client via the tenant-keyed wrappers. Keys are
// ['band', bandId, 'connections'] so a band switch scorches them AND the connection-changed / sync-completed
// plane-B signals invalidate the same shape live (the OAuth-loop close + "last synced" stamp).
//
// Tokens NEVER reach the browser: the GET returns credential-SANITIZED status only (status + handle + dates), a
// different table from the vault the serialization layer has no path to. Connect returns an authorizeUrl
// the caller opens in the SYSTEM BROWSER; disconnect empties the token AND its synced data server-side.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type ConnectionStatus = components["schemas"]["ConnectionStatus"];
type ConnectionListResponse = components["schemas"]["ConnectionListResponse"];
type OAuthStartResult = components["schemas"]["OAuthStartResult"];

/** GET the band's connection status cards (band:read — every reaching seat sees status; secrets are unserializable). */
export function useConnections() {
  const { api } = useApp();
  return useBandQuery<ConnectionListResponse>(
    ["connections"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/connections", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      // POLL WHILE A SYNC IS IN FLIGHT.
      //
      // `freshness.syncing` is derived server-side from the schedule row, so the client only learns it by
      // READING. Plane B says when a sync ENDS (`sync-completed` invalidates this key) but nothing announces
      // the START: a login-triggered wake happens during page load, and the manual button returns before the
      // scheduler has even claimed the row. Without polling, the flag flips true and back between two reads
      // and is never observed — the hero spinner never appears, and "last synced" keeps rendering the age of
      // a CACHED value rather than of the sync (both reported 2026-08-22).
      //
      // Two seconds, and only while a pull is actually running; `sync-completed` still does the real
      // invalidation when it lands.
      refetchInterval: (query) => (query.state.data?.freshness?.syncing ? 2_000 : false),
    },
  );
}

/** POST begin an OAuth connect → `{ authorizeUrl }`. The caller opens it in the system browser; the callback
 * publishes connection-changed so the card flips to Connected live (no popup poll). 404 = unsupported provider;
 * 502 = the provider's credentials aren't configured on this environment yet (the just-in-time-credential state).
 * `shopDomain` is Shopify-only: its OAuth is per-shop, so the `*.myshopify.com` domain is captured on the
 * card and threaded here; every other provider leaves it null (bare-OAuth). */
export function useStartConnection() {
  const { api } = useApp();
  return useBandMutation<OAuthStartResult, { provider: string; returnUrl?: string; shopDomain?: string }>(
    async (bandId, { provider, returnUrl, shopDomain }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/connections/{provider}/start", {
        params: { path: { bandId, provider } },
        body: { returnUrl: returnUrl ?? null, shopDomain: shopDomain ?? null },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onError: (err) => pushToast(errorMessage(err, "Couldn't start the connection")) },
  );
}

export type YouTubeConnectResult = components["schemas"]["YouTubeConnectResult"];

/** POST connect YouTube by PASTING a channel — there is no OAuth here. YouTube is a global-API-key provider,
 * so this just records which channel is the band's and births its sync schedule.
 *
 * A channel URL or bare `UC…` id needs no API call and works on an environment with no YouTube key; an
 * @handle needs a lookup and answers a typed 502 naming the missing key. Both messages are worth showing
 * verbatim — they tell the band exactly which shape to paste instead. */
export function useConnectYouTube() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<YouTubeConnectResult, { channel: string }>(
    async (band, { channel }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/connections/youtube", {
        params: { path: { bandId: band } },
        body: { channel },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (res) => {
        pushToast(res.title ? `Connected to ${res.title}.` : "YouTube channel connected.", "info");
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't connect that channel")),
    },
  );
}

export type SyncRefreshResult = components["schemas"]["SyncRefreshResult"];
export type SyncFreshness = components["schemas"]["SyncFreshness"];

/** POST "Refresh all data" — make every one of the band's sync schedules due now.
 *
 * The scheduler picks them up on its next tick, so this is a KICK, not a completed sync: the response says
 * how many were queued, and fresh numbers land a minute or so later. Rate limited server-side per band (it
 * spends real vendor quota), which surfaces here as a 429 the caller should show rather than swallow. */
export function useRefreshAllData() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SyncRefreshResult, void>(
    async (band) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/connections/refresh", {
        params: { path: { bandId: band } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (res) => {
        pushToast(
          res.queued && res.queued > 0
            ? `Refreshing ${res.queued} source${res.queued === 1 ? "" : "s"} — new numbers land shortly.`
            : "Nothing to refresh yet — connect a source first.",
          "info",
        );
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't start a refresh")),
    },
  );
}

export type SocialHandles = components["schemas"]["SocialHandles"];

/** PUT the band's public @-handles (Instagram / TikTok / Threads). These are NOT connections — they are public
 * band identity that the release pages, EPK and Link in Bio render, and a typed handle overrides the one
 * inferred from a Meta connection. The server normalizes what it stores (strips '@', accepts a pasted profile
 * URL) and answers 422 with the offending field named, so the response is the truth the card re-renders from. */
export function useUpdateSocialHandles() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SocialHandles, SocialHandles>(
    async (band, handles) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/connections/social-handles", {
        params: { path: { bandId: band } },
        body: handles,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        pushToast("Public handles saved.", "info");
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save your handles")),
    },
  );
}

export type CalendarFeedView = components["schemas"]["CalendarFeedView"];

/** GET the band's ICS subscribe-feed state (born-blank until first minted). Its own query rather than a field
 * on the connections payload: the token is a bearer URL, so it is fetched only by the seat that can manage it
 * (band:manage) instead of riding the band:read list every seat loads. */
export function useCalendarFeed() {
  const { api } = useApp();
  return useBandQuery<CalendarFeedView>(["calendar-feed"], async (bandId) => {
    const { data, error, response } = await api.GET(
      "/api/v1/bands/{bandId}/connections/calendar-feed",
      { params: { path: { bandId } } },
    );
    if (!response.ok || !data) throw new ApiError(response.status, error);
    return data;
  });
}

/** POST mint-or-ROTATE the subscribe link — the card's "Reset link". Rotation overwrites the token, so every
 * existing subscription stops working the moment it commits and the band must re-subscribe. That is
 * destructive in a way a normal save is not, which is why the card confirms before calling it. */
export function useRotateCalendarFeed() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<CalendarFeedView, { minting: boolean }>(
    async (band) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/connections/calendar-feed",
        { params: { path: { bandId: band } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, { minting }) => {
        pushToast(
          minting
            ? "Subscribe link created."
            : "Link reset — the old URL no longer works. Re-subscribe with the new one.",
          "info",
        );
        void qc.invalidateQueries({ queryKey: ["band", bandId, "calendar-feed"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't update the subscribe link")),
    },
  );
}

/** The merch sales lanes a store can be assigned to. The wire spelling the assign route binds — and the same
 * strings `ConnectionStatus.merchChannels` carries, so eligibility is filtered from the SERVER's capability
 * answer rather than from a copy of the vendor table kept here. */
export type MerchChannel = "online" | "pos";

/** PUT the store that feeds a merch sales lane — or null to clear the nomination and fall back to
 * earliest-connected. This decides which store's numbers become the dashboard's online merch figure and the
 * per-show POS gross, so it is a money-shaped write: the server re-validates capability and answers 409 when a
 * concurrent change took the lane first (the two-tab race the DB interlock exists for). */
export function useAssignMerchChannel() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { channel: MerchChannel; provider: string | null; message: string }>(
    async (band, { channel, provider }) => {
      const { error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/connections/merch-channels/{channel}",
        { params: { path: { bandId: band, channel } }, body: { provider } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_res, { message }) => {
        pushToast(message, "info");
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
      onError: (err, { channel }) => {
        pushToast(errorMessage(err, `Couldn't set the ${channel === "pos" ? "in-person" : "online"} store`));
        // A 409 means someone else moved the lane — refetch so the picker stops showing a stale winner.
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
    },
  );
}

/** DELETE a provider connection (disconnect) — deletes the vaulted token(s) + empties its synced projections
 * server-side (leak test 3). Idempotent (204). connection-changed also repaints the card, but we invalidate
 * eagerly so the disconnect is instant even without the realtime relay. */
export type SpotifyArtistHit = components["schemas"]["SpotifyArtistHit"];
export type SpotifyArtistSearchResponse = components["schemas"]["SpotifyArtistSearchResponse"];
export type SpotifyArtistClaim = components["schemas"]["SpotifyArtistClaim"];
export type SpotifyArtistReservation = components["schemas"]["SpotifyArtistReservation"];

/** GET Spotify catalogue matches for the artist picker.
 *
 * Debounced by the caller and disabled below two characters — every keystroke that reaches here is a metered
 * vendor call, and one-letter queries return noise anyway. A pasted artist link or id resolves to that single
 * artist rather than searching, so "I already know my id" stays one step. */
export function useSpotifyArtistSearch(query: string) {
  const { api } = useApp();
  const q = query.trim();
  return useBandQuery<SpotifyArtistSearchResponse>(
    ["connections", "spotify-artists", q],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/connections/spotify/artists", {
        params: { path: { bandId }, query: { q } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      enabled: q.length >= 2,
      // The catalogue does not move while a picker is open, and the server caches it anyway.
      staleTime: 5 * 60_000,
    },
  );
}

/** PUT RESERVE a Spotify artist for this band.
 *
 * The artist id is unique across the whole platform — it is the key the band's DSP analytics are resolved by —
 * so a 409 here means another account already holds it, which is a different situation from any other failure
 * and gets its message shown verbatim. A 409 before the Spotify sign-in means the gate refused: connect first. */
export function useClaimSpotifyArtist() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<SpotifyArtistClaim, { artist: string }>(
    async (band, { artist }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/connections/spotify/artist", {
        params: { path: { bandId: band } },
        body: { artist },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (res) => {
        pushToast(`Reserved ${res.name ?? "your artist"} on Spotify.`, "info");
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't reserve that artist")),
    },
  );
}

export type ChartmetricArtistHit = components["schemas"]["ChartmetricArtistHit"];
export type ChartmetricArtistSearchResponse = components["schemas"]["ChartmetricArtistSearchResponse"];
export type ChartmetricArtistLinkView = components["schemas"]["ChartmetricArtistLinkView"];
export type ChartmetricArtistClaimResult = components["schemas"]["ChartmetricArtistClaimResult"];

/** GET Chartmetric artist matches for the LINK picker.
 *
 * Same shape and the same reasons as the Spotify one: debounced by the caller, disabled below two characters,
 * because every keystroke here is a metered vendor call and Chartmetric bills per CREDIT rather than per
 * artist — so on this vendor it is the requests that cost. There is deliberately no paste-an-id path: a
 * Chartmetric artist page is not something a band has any reason to be holding. */
export function useChartmetricArtistSearch(query: string) {
  const { api } = useApp();
  const q = query.trim();
  return useBandQuery<ChartmetricArtistSearchResponse>(
    ["connections", "chartmetric-artists", q],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/connections/chartmetric/artists", {
        params: { path: { bandId }, query: { q } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: q.length >= 2, staleTime: 5 * 60_000 },
  );
}

/** PUT DECLARE the band's Chartmetric artist.
 *
 * Unlike the Spotify reservation this is not exclusive across accounts, so there is no 409 to special-case —
 * the Spotify claim is already the global fence for the analytics spine and a second one would add a dispute
 * surface without adding protection. What it DOES do is outrank every derivation permanently, so re-claiming
 * is the only way to correct a wrong pick. */
export function useClaimChartmetricArtist() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<ChartmetricArtistClaimResult, { artist: string }>(
    async (band, { artist }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/connections/chartmetric/artist", {
        params: { path: { bandId: band } },
        body: { artist },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (res) => {
        pushToast(`Linked ${res.name ?? "your artist"} on Chartmetric.`, "info");
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't link that artist")),
    },
  );
}

export function useDisconnectConnection() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { provider: string; label: string }>(
    async (band, { provider }) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/connections/{provider}", {
        params: { path: { bandId: band, provider } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_res, { label }) => {
        pushToast(`Disconnected from ${label}.`, "info");
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't disconnect")),
    },
  );
}
